import { useEffect } from "react";
import RoutesContainer from "./Development/routes";
import LoaderProvider from "./Development/components/LoaderProvider";
import DocumentTitle from "react-document-title";
import { Context } from "./Development/Context";
import { useContext } from "react";
import metaTagsData from "./Operation/metatags.json";
import { WebSocketProvider } from "./Development/Context/WebSocketContext";
import "react-image-crop/dist/ReactCrop.css";
const capitalizeFirstLetter = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

// Function to dynamically set the favicon
const setFavicon = (logoUrl) => {
  if (!logoUrl) return;
  let link =
    document.querySelector("link[rel*='icon']") ||
    document.createElement("link");
  link.type = "image/x-icon";
  link.rel = "shortcut icon";
  link.href = logoUrl;
  document.head.appendChild(link);
};

// Function to verify meta tags
const verifyMetaTags = () => {
  const metaTags = {
    title: document.querySelector('meta[name="title"]')?.content,
    description: document.querySelector('meta[name="description"]')?.content,
    keywords: document.querySelector('meta[name="keywords"]')?.content,
    documentTitle: document.title
  };

  return metaTags;
};

function App() {
  const { ready, institutionData } = useContext(Context);

  // **Company Name as Title Source**
  const companyName = institutionData?.companyName || "Cafe";
  const defaultDocumentTitle = `Welcome to ${capitalizeFirstLetter(companyName)}`;

  // **Meta Tags Title, Description, and Keywords**
  const metaTitle =
    metaTagsData?.seo?.title ||
    metaTagsData?.title ||
    institutionData?.seo?.title ||
    defaultDocumentTitle;

  const metaDescription =
    metaTagsData?.seo?.description ||
    metaTagsData?.description ||
    institutionData?.seo?.description ||
    institutionData?.description ||
    "Default description for the website.";

  const rawKeywords =
    metaTagsData?.seo?.keywords ||
    metaTagsData?.keywords ||
    institutionData?.seo?.keywords;

  const metaKeywords = Array.isArray(rawKeywords)
    ? rawKeywords.join(", ")
    : (typeof rawKeywords === "string" && rawKeywords.trim().length > 0
        ? rawKeywords
        : "default, keywords");

  const gtmId =
    metaTagsData?.gtmId ||
    metaTagsData?.GTM_ID ||
    metaTagsData?.gtm_id ||
    institutionData?.GTM_ID;

  useEffect(() => {
    // Update Meta Tags in <head>
    const updateOrCreateMetaTag = (name, content) => {
      if (!content) return;
      let tag = document.querySelector(`meta[name='${name}']`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.name = name;
        document.head.appendChild(tag);
      }
      tag.content = content;
    };

    const updateOrCreateMetaProperty = (property, content) => {
      if (!content) return;
      let tag = document.querySelector(`meta[property='${property}']`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    };

    // Set meta title, description, and keywords
    updateOrCreateMetaTag("title", metaTitle);
    updateOrCreateMetaTag("description", metaDescription);
    updateOrCreateMetaTag("keywords", metaKeywords);

    // Open Graph tags
    updateOrCreateMetaProperty("og:title", metaTitle);
    updateOrCreateMetaProperty("og:description", metaDescription);

    // Set document title
    if (metaTitle) {
      document.title = metaTitle;
    }

    // Set the favicon dynamically
    setFavicon(institutionData?.logoUrl);

    // Verify meta tags after they're set
    setTimeout(() => {
      verifyMetaTags();
    }, 1000);
  }, [metaTitle, metaDescription, metaKeywords, institutionData?.logoUrl]);

  // GTM Setup
  useEffect(() => {
    if (gtmId) {
      // Remove existing GTM script if any
      const existingScript = document.querySelector(
        'script[src*="googletagmanager"]'
      );
      if (existingScript) existingScript.remove();

      const existingNoscript = document.querySelector(
        'iframe[src*="googletagmanager"]'
      );
      if (existingNoscript && existingNoscript.parentElement) {
        existingNoscript.parentElement.remove();
      }

      // Add GTM script dynamically
      const script = document.createElement("script");
      script.innerHTML = `
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${gtmId}');
      `;
      document.head.appendChild(script);

      // Add noscript fallback dynamically
      const noscript = document.createElement("noscript");
      noscript.innerHTML = `
        <iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}"
        height="0" width="0" style="display:none;visibility:hidden"></iframe>
      `;
      document.body.appendChild(noscript);
    }
  }, [gtmId]);

  if (ready) {
    document.body.style.setProperty(
      "--primary-color",
      institutionData.PrimaryColor
    );
    document.body.style.setProperty(
      "--secondary-color",
      institutionData.SecondaryColor
    );
    document.body.style.setProperty(
      "--light-primary-color",
      institutionData.LightPrimaryColor
    );
    return (
      <LoaderProvider>
        <DocumentTitle title={metaTitle || defaultDocumentTitle}>
        <WebSocketProvider>
            <RoutesContainer />
          </WebSocketProvider>
        </DocumentTitle>
      </LoaderProvider>
    );
  } else {
    return <div></div>;
  }
}

export default App;
